import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Stage.css';
import stageJpg from '../../assets/images/stage.jpg';
import { storedQuality, weakDevice, rememberLite, onQualityChange, measureFrames, fps, screenRate, tooSlow } from './quality';

/*
    Sfondo della partita: lo studio dell'Intesa Vincente, tutto in SVG e CSS (nessuna immagine da scaricare, salvo il
    ripiego per i dispositivi lenti).
    È l'unico punto d'ingresso: Game lo monta sul display mentre la partita è in corso e qui nascono tutte le parti
    (Scene.js).

    Sta dentro .App, dietro all'interfaccia (come .buttonBg, z-index -1), e non dentro .game: .game viene scalato da
    scaleElements e lo sfondo deve invece coprire lo schermo intero. La scena è disegnata in 1280x720 e viene scalata
    come "cover", centrata. Il codice della scena è in un pezzo separato del bundle, scaricato solo quando serve.

    STATO INIZIALE: all'istante 0 delle animazioni la scena è quella con cui finisce il video delle cutscene (faretti
    dritti, pilastro fermo, nessuna scritta sulla fascia), vista attraverso un velo sfocato: il video finisce infatti
    su stage_iniziale_blur.png. Quando il video finisce (anche se viene saltato), nello stesso istante in cui sparisce:
    - tutte le animazioni tornano all'istante 0 e ripartono da lì;
    - il velo (backdrop-filter, il blur "di riflesso" su un livello trasparente sopra la scena) si accende e la
      scritta della fascia viene nascosta (classe st-veiled).
    Il velo sfuma insieme al layer "GIOCANO: [squadra]" (.curTeam) quando questo inizia a sparire, oppure dopo
    VEIL_MAX ms se il layer non arriva; subito dopo la scritta compare con una dissolvenza (classe st-revealed).

    ULTIMA CUTSCENE (prop final, quella prima dei risultati finali): il layer "GIOCANO" non c'è e il velo non sfuma
    mai, resta per tutti i risultati finali (classe st-final). Non c'è niente da aspettare, quindi la scena parte
    subito: la scritta compare con la dissolvenza già dal primo istante, sotto il velo.

    DISPOSITIVI LENTI (quality.js): al posto della scena c'è stage.jpg, un suo fotogramma fermo, scaricato solo da chi
    lo usa. Se i controlli iniziali bastano a dirlo, la scena non viene nemmeno scaricata. Altrimenti parte la scena e
    dopo ogni cutscene, sotto il velo, si contano i fotogrammi che riesce a disegnare: se sono troppo pochi l'immagine
    sfuma sopra la scena, che poi viene smontata. Il cambio avviene solo sotto il velo, con la scena sfocata: se
    l'immagine non è pronta in tempo, alla fine della cutscene successiva. Il velo resta anche sopra l'immagine.
    Scelta a mano dal toggle delle impostazioni, la modalità cambia subito, in entrambi i versi: verso l'immagine come
    sopra; verso la scena montandola sotto l'immagine, che sfuma via appena la scena è nel DOM e poi viene smontata.
*/

const Scene = lazy(() => import('./Scene'));
const SW = 1280, SH = 720;
const BLUR = 8;         //raggio del blur in unità della scena: 12px a 1080p, come nell'immagine con cui finisce il video
const VEIL_MAX = 2600;  //il layer "GIOCANO" inizia a sparire circa 1,7 s dopo la fine del video
const VEIL_FADE = 300;  //ms: la dissolvenza del velo (Stage.css), dopo cui la scritta inizia a comparire (Scene.css)
const PROBE_DELAY = 300;    //ms dopo la fine del video: si saltano i primi fotogrammi, appesantiti dal taglio
const PROBE_MS = 900;       //la misura finisce presto, così l'immagine ha tempo di arrivare prima che il velo sfumi

//avvisa Stage quando la scena è davvero nel DOM (il suo pezzo è arrivato): solo allora l'immagine può sparire
function Mounted({ onChange }) {
    useEffect(() => {
        onChange(true);
        return () => onChange(false);
    }, [onChange]);
    return null;
}

export default function Stage({ final = false }) {
    const ref = useRef(null);
    const sceneRef = useRef(null);
    const finalRef = useRef(final);
    finalRef.current = final;

    //"lite": l'immagine; "full": la scena senza misure; "auto": la scena, misurata dopo ogni cutscene
    const [quality, setQuality] = useState(storedQuality);
    const qualityRef = useRef(quality);
    qualityRef.current = quality;
    const [scene, setScene] = useState(false);                  //la scena è montata
    const [ready, setReady] = useState(false);                  //la scena è nel DOM
    const [still, setStill] = useState(quality === 'lite');     //l'immagine ferma è montata
    const [shown, setShown] = useState(false);                  //l'immagine è caricata: sfuma sopra la scena
    const probing = useRef(false);
    probing.current = quality === 'auto' && scene && !still;
    //la scena è tornata (scelta a mano): l'immagine sfuma via
    const leaving = shown && ready && quality !== 'lite';

    //"lite": l'immagine sfuma sopra la scena. Altrimenti parte la scena: con "auto" dopo che è stata misurata la frequenza
    //dello schermo, a scena spenta (una volta per sessione, sotto lo splash); intanto si scarica il suo pezzo. Un
    //dispositivo debole già a priori non la monta nemmeno: il verdetto viene salvato e si passa all'immagine
    useEffect(() => {
        if (quality === 'lite') {
            setStill(true);
            return;
        }
        if (quality === 'auto' && weakDevice()) {
            rememberLite();
            setQuality('lite');
            return;
        }
        let alive = true;
        import('./Scene');
        (quality === 'auto' ? screenRate() : Promise.resolve()).then(() => {
            if (alive) setScene(true);
        });
        return () => {
            alive = false;
        };
    }, [quality]);

    //scelta a mano dal toggle delle impostazioni: si applica subito
    useEffect(() => onQualityChange((value, manual) => {
        if (manual) setQuality(value);
    }), []);

    useEffect(() => {
        const root = ref.current;
        //la scala si scrive direttamente sullo stile: un nuovo render della scena a ogni resize non serve
        const fit = () => {
            const box = root?.getBoundingClientRect();
            if (!box || !sceneRef.current) return;
            const k = Math.max(box.width / SW, box.height / SH);
            sceneRef.current.style.transform = `translate(${(box.width - SW * k) / 2}px, ${(box.height - SH * k) / 2}px) scale(${k})`;
            root.style.setProperty('--st-blur', (BLUR * k).toFixed(2) + 'px');
        };
        fit();
        window.addEventListener('resize', fit);

        const animations = () => sceneRef.current?.getAnimations ? sceneRef.current.getAnimations({ subtree: true }) : [];
        const covered = () => root.classList.contains("st-veiled") || root.classList.contains("st-final");
        let alive = true, veilTimer = null, probeTimer = null, swapReady = false;

        //il velo sfuma e la scritta compare. Il rullino riparte da capo: dopo la dissolvenza è nella posizione più
        //leggibile (vedi st-spin in Scene.css); il salto non si vede perché finora la scritta era nascosta
        const reveal = () => {
            clearTimeout(veilTimer);
            if (!root.classList.contains("st-veiled")) return;
            root.classList.replace("st-veiled", "st-revealed");
            animations().forEach((animation) => {
                if (animation.animationName === "st-spin") animation.currentTime = 0;
            });
        };

        //quanti fotogrammi riesce a disegnare la scena, ora che si vede. Se sono troppo pochi l'immagine si scarica e,
        //appena pronta, prende il posto della scena: subito se c'è ancora il velo, se no alla fine della prossima cutscene.
        //Nel frattempo la modalità può essere stata scelta a mano: allora non si cambia nulla
        const probe = async () => {
            const veiled = covered();
            const gaps = await measureFrames(PROBE_MS);
            if (!gaps || !alive || !probing.current || !tooSlow(fps(gaps), await screenRate(), veiled)) return;
            rememberLite();
            const img = new Image();
            img.src = stageJpg;
            (img.decode ? img.decode() : Promise.resolve()).then(() => {
                if (!alive || qualityRef.current !== 'auto') return;
                if (covered()) setQuality('lite');
                else swapReady = true;
            }, () => {});
        };

        //'ended' non risale il DOM: si ascolta in fase di cattura, quindi prima che CutScene nasconda il video
        const restart = (event) => {
            if (!event.target.classList?.contains("cutscene-video")) return;
            const last = finalRef.current;
            root.classList.remove("st-revealed", "st-veiled", "st-final");
            root.classList.add(last ? "st-final" : "st-veiled");
            animations().forEach((animation) => {
                //nei risultati finali la scritta non aspetta il velo: il rullino parte avanti del ritardo che avrebbe
                //dopo st-revealed, così alla fine della dissolvenza è nella stessa posizione
                animation.currentTime = last && animation.animationName === "st-spin" ? VEIL_FADE : 0;
            });
            clearTimeout(veilTimer);
            if (!last) veilTimer = setTimeout(reveal, VEIL_MAX);
            clearTimeout(probeTimer);
            if (swapReady && qualityRef.current === 'auto') setQuality('lite');
            else if (probing.current) probeTimer = setTimeout(probe, PROBE_DELAY);
        };

        //il layer "GIOCANO" inizia a sparire: il velo sfuma insieme a lui
        const teamLeaving = (event) => {
            if (event.animationName === "fadeout" && event.target.classList?.contains("curTeam")) reveal();
        };

        document.addEventListener('ended', restart, true);
        document.addEventListener('animationstart', teamLeaving, true);

        return () => {
            alive = false;
            clearTimeout(veilTimer);
            clearTimeout(probeTimer);
            window.removeEventListener('resize', fit);
            document.removeEventListener('ended', restart, true);
            document.removeEventListener('animationstart', teamLeaving, true);
        };
    }, []);

    //fine di una dissolvenza dell'immagine: comparsa → la scena sotto non serve più; scomparsa → via l'immagine
    const stillFaded = (event) => {
        if (event.animationName === "st-still-out") {
            setStill(false);
            setShown(false);
        } else if (quality === 'lite') {
            setScene(false);
        }
    };

    const app = document.querySelector('.App');
    if (!app) return null;

    return createPortal(
        <div className="st-stage" ref={ref} aria-hidden="true">
            <div className="st-stg" ref={sceneRef}>
                {scene && (
                    <Suspense fallback={null}>
                        <Scene />
                        <Mounted onChange={setReady} />
                    </Suspense>
                )}
            </div>
            {/* se nel frattempo è stata scelta la scena, l'immagine appena arrivata non serve */}
            {still && <img className={"st-still" + (shown ? " st-shown" : "") + (leaving ? " st-leaving" : "")} src={stageJpg}
                alt="" decoding="async" onLoad={() => (quality === 'lite' ? setShown(true) : setStill(false))} onAnimationEnd={stillFaded} />}
            <div className="st-veil"></div>
        </div>,
        app
    );
}
